import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarDays, MapPin } from 'lucide-react';
import { useEvents, useHousehold } from '@/lib/queries';
import { api, ApiError } from '@/lib/api';
import { dateTime, money } from '@/lib/format';
import { ErrorNote, Spinner } from '@/components/Layout';

interface Eligible { person_id: string; name: string; band: string | null; options: { id: string; name: string; price_pence: number }[]; reason: string | null; reason_text: string | null }

/** Book an event for people in the household. What each person can book, and the price, come from the server. */
export function BookEvent() {
  const { id = '' } = useParams();
  const events = useEvents();
  const household = useHousehold();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [people, setPeople] = useState<Eligible[] | null>(null);
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const event = events.data?.find(e => e.id === id);

  useEffect(() => {
    api<{ people: Eligible[] }>('/events/eligibility', { method: 'POST', body: { event_id: id } })
      .then(r => setPeople(r.people))
      .catch(() => setError('We could not load booking options for this event.'));
  }, [id]);

  const chosen = useMemo(() => Object.entries(choice).filter(([, o]) => o), [choice]);
  const total = useMemo(() => chosen.reduce((s, [pid, oid]) => s + (people?.find(p => p.person_id === pid)?.options.find(o => o.id === oid)?.price_pence ?? 0), 0), [chosen, people]);

  if (household.isLoading || events.isLoading) return <Spinner />;
  if (!household.data) return <Navigate to="/join" replace />;
  if (!event) return <ErrorNote>This event isn't available for booking.</ErrorNote>;

  async function book(method: 'online' | 'paypal' | 'on_the_day' = 'online') {
    setBusy(true); setError('');
    try {
      const r = await api<{ url?: string; confirmed?: boolean; pay_on_day?: boolean }>('/checkout/event', { method: 'POST', body: { event_id: id, method, bookings: chosen.map(([person_id, option_id]) => ({ person_id, option_id })) } });
      if (r.url) { window.location.assign(r.url); return; }
      await qc.invalidateQueries({ queryKey: ['my-bookings'] });
      navigate(r.pay_on_day ? '/home?booked=otd' : '/home?booked=1');
    } catch (e) {
      setError(e instanceof ApiError && e.status === 409 ? 'Someone you chose can no longer be booked (the event may be full). Please refresh and try again.' : 'We could not start the booking. Please try again in a moment.');
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <Link to="/events" className="text-sm text-ink-500 hover:underline">← Events</Link>
        <h1 className="mt-2 text-2xl font-bold"><Link to={`/events/${event.slug}`} className="hover:underline">{event.title}</Link></h1>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink-500">
          <span className="flex items-center gap-1.5"><CalendarDays size={15} />{dateTime(event.starts_at)}</span>
          {event.venue && <span className="flex items-center gap-1.5"><MapPin size={15} />{event.venue}</span>}
        </div>
      </div>

      {!people && !error && <Spinner />}
      {people && (
        <ul className="card divide-y divide-stone-100 p-0">
          {people.map(p => (
            <li key={p.person_id} className="px-5 py-4">
              <p className="font-medium">{p.name}{p.band && <span className="ml-2 text-sm font-normal capitalize text-ink-500">{p.band}</span>}</p>
              {p.reason ? (
                <p className="mt-1 text-sm text-ink-500">{p.reason_text}{p.reason === 'needs_membership' && <> · <Link to="/renew" className="text-brand-700 underline">Get a membership</Link></>}</p>
              ) : p.options.length === 0 ? (
                <p className="mt-1 text-sm text-ink-500">No options for their age group.</p>
              ) : (
                <fieldset className="mt-2 grid gap-2 sm:grid-cols-2">
                  <legend className="sr-only">Options for {p.name}</legend>
                  <label className="flex items-center gap-2 rounded-sm border border-stone-200 px-3 py-2 text-sm">
                    <input type="radio" name={p.person_id} checked={!choice[p.person_id]} onChange={() => setChoice({ ...choice, [p.person_id]: '' })} className="accent-brand-600" />
                    Not going
                  </label>
                  {p.options.map(o => (
                    <label key={o.id} className={`flex items-center gap-2 rounded-sm border px-3 py-2 text-sm ${choice[p.person_id] === o.id ? 'border-brand-500 bg-brand-50' : 'border-stone-200'}`}>
                      <input type="radio" name={p.person_id} checked={choice[p.person_id] === o.id} onChange={() => setChoice({ ...choice, [p.person_id]: o.id })} className="accent-brand-600" />
                      <span className="flex-1">{o.name}</span><span className="font-semibold">{o.price_pence ? money(o.price_pence) : 'Free'}</span>
                    </label>
                  ))}
                </fieldset>
              )}
            </li>
          ))}
        </ul>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}
      {chosen.length > 0 && (
        <div className="card flex flex-wrap items-center justify-between gap-3">
          <p><span className="text-sm text-ink-500">{chosen.length} {chosen.length === 1 ? 'person' : 'people'} · </span><span className="text-lg font-bold">{total ? money(total) : 'Free'}</span></p>
          <span className="flex flex-wrap gap-2">
            {total > 0 && event.allow_pay_on_day && <button className="btn-ghost border border-stone-300" disabled={busy} onClick={() => book('on_the_day')}>Pay my instructor or on the day</button>}
            {total > 0 && <button className="btn-ghost border border-stone-300" disabled={busy} onClick={() => book('paypal')}>Pay with PayPal or card</button>}
            <button className="btn-primary" disabled={busy} onClick={() => book()}>{busy ? 'One moment…' : total ? 'Pay by bank now' : 'Confirm booking'}</button>
          </span>
        </div>
      )}
      {total > 0 && <p className="text-xs text-ink-500">Paying by bank goes through GoCardless; PayPal also takes debit and credit cards. Your bank or card details go to them, never to EGKA's website.{event.allow_pay_on_day && ' If you pay your instructor or on the day instead, your place is held and the Membership Secretary marks it paid when the money reaches EGKA.'}</p>}
    </div>
  );
}
